import { useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import type { Zone, ZonePayload } from '@/api/zonesService';
import type { SportRef } from '@/api/referenceService';

interface ZoneModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: ZonePayload) => Promise<unknown>;
  zone: Zone | null;
  sports: SportRef[];
  isSaving: boolean;
}

const empty = { label: '', identifier: '', sportIdentifier: '', pctIcMin: '', pctIcMax: '' };

const ZoneModal = ({ open, onClose, onSubmit, zone, sports, isSaving }: ZoneModalProps) => {
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      setForm(
        zone
          ? {
              label: zone.label,
              identifier: zone.identifier,
              sportIdentifier: zone.sportIdentifier,
              pctIcMin: String(zone.pctIcMin),
              pctIcMax: String(zone.pctIcMax),
            }
          : empty
      );
    }
  }, [open, zone]);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async () => {
    setError('');
    const min = Number(form.pctIcMin);
    const max = Number(form.pctIcMax);
    if (!form.label.trim()) return setError('Le libellé est requis.');
    if (!form.sportIdentifier) return setError('Le sport est requis.');
    if (!(min > 0) || !(max > 0)) return setError('Les valeurs %IC doivent être supérieures à 0.');
    if (max < min) return setError('La valeur max doit être supérieure ou égale à la valeur min.');
    try {
      await onSubmit({
        label: form.label.trim(),
        identifier: form.identifier.trim(),
        sportIdentifier: form.sportIdentifier,
        pctIcMin: min,
        pctIcMax: max,
      });
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={zone ? 'Modifier la zone' : 'Ajouter une zone'}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Libellé" placeholder="Zone 7" value={form.label} onChange={set('label')} />
        <Input
          label="Identifiant"
          placeholder="ZONE7 (auto depuis le libellé si vide)"
          value={form.identifier}
          onChange={set('identifier')}
        />
        <Select label="Sport" value={form.sportIdentifier} onChange={set('sportIdentifier')}>
          <option value="">— Sélectionner —</option>
          {sports.map((s) => (
            <option key={s.identifier} value={s.identifier}>
              {s.label}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-2 gap-4">
          <Input label="%IC min" type="number" value={form.pctIcMin} onChange={set('pctIcMin')} />
          <Input label="%IC max" type="number" value={form.pctIcMax} onChange={set('pctIcMax')} />
        </div>
        {error && <p className="text-sm text-error-500">{error}</p>}
      </div>
    </Modal>
  );
};

export default ZoneModal;
